import React, { useEffect } from 'react';
import { 
  History, Award, Users, Target,
  Sun, GraduationCap, Briefcase, Home, DollarSign, Building2
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const HistoriaPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const pillars = [
    {
      icon: Home,
      title: 'Soberanía Familiar',
      description: 'Que cada hogar paraguayo disfrute de energía verde, propia y gratuita. El "corte de luz" debe ser un recuerdo del pasado.'
    },
    {
      icon: DollarSign,
      title: 'Blindaje Económico',
      description: 'Reducir drásticamente el gasto en tarifas eléctricas. El sol no envía facturas, y nosotros hacemos que trabaje para vos.'
    },
    {
      icon: Target,
      title: 'Anticipación al Futuro',
      description: 'El consumo energético mundial crece exponencialmente. Zenex llega para solucionar los problemas antes de que se vuelvan críticos.'
    }
  ];

  const florenciaEducation = [
    { degree: 'Bachelor of Science in Agricultural Technology Management', emphasis: 'Énfasis en Water Management', institution: 'Kansas State University, USA' },
    { degree: 'Bachelor of Science in Natural Resources and Environmental Science', emphasis: null, institution: 'Kansas State University, USA' },
    { degree: 'Minor in Business', emphasis: null, institution: 'Kansas State University, USA' },
    { degree: 'Máster en Dirección de Proyectos Ambientales y Responsabilidad Social Corporativa', emphasis: null, institution: 'EUDE Business School, España' }
  ];

  const matiasEducation = [
    { degree: 'Bachelor of Science in Business Administration', emphasis: 'Especializado en Entrepreneurship & Innovation', institution: 'Kansas State University, USA' },
    { degree: 'Minor in Marketing Technology', emphasis: null, institution: 'Kansas State University, USA' },
    { degree: 'Máster en Gestión de Negocios Digitales', emphasis: null, institution: 'EUDE Business School, España' }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="historia-page">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-zinc-900 via-zinc-800 to-emerald-900 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-20 w-72 h-72 bg-emerald-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-emerald-400/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-center gap-2 text-emerald-400 mb-6">
            <History className="w-5 h-5" />
            <span className="text-sm font-medium tracking-wider uppercase">Nuestra Historia</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            La Evolución de la
            <span className="block text-emerald-400">Energía en Paraguay</span>
          </h1>
          
          <p className="text-xl text-zinc-300 max-w-2xl">
            Conocé el camino de los fundadores que los llevó a crear Zenex, 
            y las enormes metas que tienen para transformar el futuro energético del país.
          </p>
        </div>
      </section>

      {/* El Origen */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-zinc-900 mb-6">El Origen de una Revolución</h2>
            
            <p className="text-zinc-600 mb-6 text-lg leading-relaxed">
              Paraguay es un país bendecido por el sol, pero atrapado en una paradoja: mientras nuestra 
              tierra arde de energía, nuestras familias y empresas siguen siendo rehenes de una red 
              eléctrica saturada, costos crecientes y la incertidumbre de la oscuridad.
            </p>

            <p className="text-zinc-600 text-lg leading-relaxed">
              En <strong className="text-emerald-600">2026</strong>, nació Zenex. No como una empresa más, 
              sino como una respuesta audaz a una pregunta urgente: <em>¿Por qué esperar a que el futuro 
              mejore si podemos construirlo nosotros mismos?</em>
            </p>
          </div>
        </div>
      </section>

      {/* Fundadores */}
      <section className="py-20 bg-zinc-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Los Fundadores
            </h2>
            <p className="text-lg text-zinc-600">
              Profesionales que regresaron a su tierra con el conocimiento global necesario para elevar el estándar de vida local
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Florencia */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-zinc-100">
              {/* Photo */}
              <div className="relative h-96 overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_zenex-solar-1/artifacts/ofe3dy5k_Foto%20Perfil%20Flopy.jpeg"
                  alt="Florencia Samudio"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-4 left-6 right-6">
                  <h3 className="text-3xl font-bold text-white">Florencia Samudio</h3>
                  <p className="text-emerald-400 font-semibold text-lg">La Arquitecta del Cambio Técnico</p>
                </div>
              </div>
              
              <div className="p-8">
                <p className="text-zinc-600 mb-6 leading-relaxed">
                  Florencia es el motor científico de Zenex. Su preparación académica internacional le permite 
                  abordar los desafíos ambientales con una precisión técnica sin precedentes en el mercado local.
                </p>

                {/* Formación Académica */}
                <div className="mb-6">
                  <h4 className="font-bold text-zinc-900 mb-4 flex items-center gap-2 text-lg">
                    <GraduationCap className="w-6 h-6 text-emerald-600" />
                    Formación Académica
                  </h4>
                  <div className="space-y-4">
                    {florenciaEducation.map((edu, idx) => (
                      <div key={idx} className="relative pl-6 border-l-4 border-emerald-500 bg-gradient-to-r from-emerald-50 to-transparent py-3 pr-4 rounded-r-lg">
                        <p className="font-bold text-zinc-900">{edu.degree}</p>
                        {edu.emphasis && <p className="text-emerald-600 text-sm font-medium">{edu.emphasis}</p>}
                        <p className="text-zinc-500 text-sm flex items-center gap-1 mt-1">
                          <span className="inline-block w-2 h-2 bg-emerald-400 rounded-full"></span>
                          {edu.institution}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Liderazgo Comprobado */}
                <div className="mb-6">
                  <h4 className="font-bold text-zinc-900 mb-3 flex items-center gap-2 text-lg">
                    <Building2 className="w-6 h-6 text-emerald-600" />
                    Liderazgo Comprobado
                  </h4>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                    <p className="text-zinc-700">
                      Actualmente tiene a su cargo el <strong>Departamento de Desarrollo e Investigación</strong> de 
                      una empresa destacada en Paraguay, donde propone <strong>proyectos innovadores de gran escala</strong> y 
                      lidera la resolución de problemas de <strong>gran envergadura para el país</strong>.
                    </p>
                  </div>
                </div>

                <div className="text-zinc-700 italic border-l-4 border-emerald-500 pl-4 bg-emerald-50 py-4 rounded-r-lg mb-6">
                  <p>
                    "Florencia no se detiene a esperar soluciones; <strong>ella las diseña</strong>. Su capacidad para gestionar 
                    recursos naturales y su enfoque en la sostenibilidad aseguran que cada proyecto de Zenex 
                    sea técnicamente impecable y éticamente sólido."
                  </p>
                </div>

                {/* LinkedIn */}
                <a 
                  href="https://www.linkedin.com/in/eliana-samudio/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#0077B5] text-white font-medium rounded-lg hover:bg-[#006399] transition-colors"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  Ver perfil en LinkedIn
                </a>
              </div>
            </div>

            {/* Matias */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-zinc-100">
              {/* Photo */}
              <div className="relative h-96 overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_zenex-solar-1/artifacts/uqp8onvc_IMG_9440%20cuadrada%20copy.jpg"
                  alt="Matias Salama"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-4 left-6 right-6">
                  <h3 className="text-3xl font-bold text-white">Matias Salama</h3>
                  <p className="text-blue-400 font-semibold text-lg">El Estratega de la Innovación</p>
                </div>
              </div>
              
              <div className="p-8">
                <p className="text-zinc-600 mb-6 leading-relaxed">
                  Matias aporta la visión de escala y la disrupción empresarial necesaria para que la 
                  tecnología llegue a cada hogar. Su trayectoria habla de un líder nato con enfoque en el impacto real.
                </p>

                {/* Formación Académica */}
                <div className="mb-6">
                  <h4 className="font-bold text-zinc-900 mb-4 flex items-center gap-2 text-lg">
                    <GraduationCap className="w-6 h-6 text-blue-600" />
                    Formación Académica
                  </h4>
                  <div className="space-y-4">
                    {matiasEducation.map((edu, idx) => (
                      <div key={idx} className="relative pl-6 border-l-4 border-blue-500 bg-gradient-to-r from-blue-50 to-transparent py-3 pr-4 rounded-r-lg">
                        <p className="font-bold text-zinc-900">{edu.degree}</p>
                        {edu.emphasis && <p className="text-blue-600 text-sm font-medium">{edu.emphasis}</p>}
                        <p className="text-zinc-500 text-sm flex items-center gap-1 mt-1">
                          <span className="inline-block w-2 h-2 bg-blue-400 rounded-full"></span>
                          {edu.institution}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Liderazgo Comprobado */}
                <div className="mb-6">
                  <h4 className="font-bold text-zinc-900 mb-3 flex items-center gap-2 text-lg">
                    <Users className="w-6 h-6 text-blue-600" />
                    Liderazgo Comprobado
                  </h4>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <p className="text-zinc-700">
                      <strong>Expresidente del Centro de Estudiantes</strong> y de la <strong>Asociación de Paraguayos en Kansas</strong>, 
                      demostrando su capacidad para unir comunidades tras un objetivo común.
                    </p>
                  </div>
                </div>

                <div className="text-zinc-700 italic border-l-4 border-blue-500 pl-4 bg-blue-50 py-4 rounded-r-lg mb-6">
                  <p>
                    "Tras impulsar el crecimiento en su empresa familiar de <strong>30+ años de experiencia</strong> en el mercado, Matias 
                    entiende que la verdadera innovación es aquella que anticipa las necesidades del mañana."
                  </p>
                </div>

                {/* LinkedIn */}
                <a 
                  href="https://www.linkedin.com/in/matias-salama/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#0077B5] text-white font-medium rounded-lg hover:bg-[#006399] transition-colors"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  Ver perfil en LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3 Pilares */}
      <section className="py-20 bg-emerald-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Nuestro Compromiso: Los 3 Pilares Zenex
            </h2>
            <p className="text-lg text-emerald-200 max-w-2xl mx-auto">
              Juntos, Florencia y Matias han diseñado un modelo de negocio bajo tres promesas innegociables
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {pillars.map((pillar, index) => {
              const Icon = pillar.icon;
              return (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center">
                  <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4">{pillar.title}</h3>
                  <p className="text-emerald-100">{pillar.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Un Antes y un Después */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-6">
            Un Antes y un Después
          </h2>
          
          <p className="text-xl text-zinc-600 mb-8 leading-relaxed">
            Zenex es más que tecnología solar; es un <strong>movimiento</strong> liderado por profesionales 
            que regresaron a su tierra con el conocimiento global necesario para elevar el estándar de vida local. 
          </p>

          <p className="text-2xl font-bold text-emerald-600 mb-8">
            Somos jóvenes, somos expertos y estamos aquí para lograr lo que otros dijeron que era imposible.
          </p>

          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-8 border border-emerald-100">
            <p className="text-xl text-zinc-700 italic">
              "Bienvenido a la soberanía energética. <strong className="text-emerald-600">Bienvenido a Zenex.</strong>"
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-emerald-600 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Sé parte de nuestra historia
          </h2>
          <p className="text-emerald-100 mb-8">
            Sumate a los paraguayos que ya generan su propia energía limpia
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button 
              onClick={() => navigate('/calculadora')}
              className="px-8 py-4 bg-white text-emerald-600 font-semibold rounded-xl hover:bg-emerald-50 transition-all"
            >
              Calcular Mi Sistema
            </button>
            <Link 
              to="/nosotros/mision-vision"
              className="px-8 py-4 bg-emerald-800/50 text-white font-semibold rounded-xl hover:bg-emerald-800 transition-all"
            >
              Ver Misión y Visión
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default HistoriaPage;
